import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.#leadWithVariantMedia(newMediaGallery);

    this.replaceWith(newMediaGallery);
  };

  /**
   * Brings the selected variant's media to the front of the incoming gallery.
   *
   * The gallery is rendered server-side in the product's NATURAL media order, so that a plain page
   * load always shows the primary product image rather than the first-available variant's image.
   * The variant's image is surfaced here instead - only in response to a real variant:update event,
   * which by definition cannot fire on page load.
   *
   * This runs on the detached element BEFORE it is inserted, so the slideshow initialises already
   * pointing at the correct slide rather than being reshuffled after the fact.
   *
   * @param {Element} gallery - The incoming media gallery, not yet connected to the document.
   */
  #leadWithVariantMedia(gallery) {
    if (!(gallery instanceof HTMLElement)) return;

    const index = Number.parseInt(gallery.dataset.variantMediaIndex ?? '', 10);

    // -1 = this variant has no media of its own. 0 = it is already the first item.
    if (!Number.isInteger(index) || index <= 0) return;

    // Carousel (mobile): open the slideshow on the variant's slide. Setting the attribute is safer
    // than reordering slides, whose CSS scroll timelines are bound to their original index.
    gallery.querySelector('slideshow-component')?.setAttribute('initial-slide', String(index));

    // Grid (desktop): hoist the variant's media to the top of the stack.
    const grid = gallery.querySelector('.media-gallery__grid');
    const target = grid?.children[index];

    if (grid && target) grid.prepend(target);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
